/* eslint-disable no-console */
import TagManager from '@sooro-io/react-gtm-module';
import {
  AnalyticsConfig,
  AnalyticsEvent,
  GeneralEvents,
  PageViewEvent,
  UserIdentity
} from '../types';
import { sanitizeUserProperties, shouldInitializeAnalytics } from '../utils';

let googleTagManagerInitialized = false;

export const initGoogleTagManager = (
  config: AnalyticsConfig,
  user?: UserIdentity
): void => {
  if (googleTagManagerInitialized) {
    return;
  }

  const shouldInit =
    shouldInitializeAnalytics(user) &&
    config.googleTagManager?.enabled !== false;

  if (shouldInit && config.googleTagManager?.gtmId) {
    try {
      TagManager.initialize({
        gtmId: config.googleTagManager.gtmId
      });
      googleTagManagerInitialized = true;
    } catch (error) {
      console.warn('Failed to initialize Google Tag Manager:', error);
    }
  }
};

export const identifyUser = (user: UserIdentity): void => {
  if (!googleTagManagerInitialized || !user.id) return;

  try {
    const properties = sanitizeUserProperties(user);
    TagManager.dataLayer({
      dataLayer: {
        event: 'identified',
        userId: user.id,
        ...properties
      },
      dataLayerName: 'personalInformation'
    });
  } catch (error) {
    console.warn('Failed to identify user in Google Tag Manager:', error);
  }
};

export const trackEvent = (event: AnalyticsEvent): void => {
  if (!googleTagManagerInitialized) return;

  try {
    TagManager.dataLayer({
      dataLayer: {
        event: event.name,
        ...event.properties
      }
    });
  } catch (error) {
    console.warn('Failed to track event in Google Tag Manager:', error);
  }
};

export const trackPageView = (event: PageViewEvent): void => {
  if (!googleTagManagerInitialized) return;

  try {
    const path = event.path || window.location.pathname;
    TagManager.dataLayer({
      dataLayer: {
        event: GeneralEvents.PAGE_VISIT,
        page: path,
        ...event.properties
      }
    });
  } catch (error) {
    console.warn('Failed to track page view in Google Tag Manager:', error);
  }
};

export const resetGoogleTagManager = (): void => {
  if (googleTagManagerInitialized) {
    try {
      TagManager.dataLayer({
        dataLayer: {
          event: 'reset',
          userId: null
        }
      });
      googleTagManagerInitialized = false;
    } catch (error) {
      console.warn('Failed to reset Google Tag Manager:', error);
    }
  }
};

export { TagManager };
export const getGoogleTagManagerInitialized = (): boolean =>
  googleTagManagerInitialized ||
  (typeof window !== 'undefined' && (window as any).dataLayer);
